import { useEffect, useMemo, useState } from "react";
import { geoMercator, geoPath } from "d3-geo";

// Map user-facing state names to the GeoJSON NAME_1 values
const NAME_ALIASES = { Odisha: "Orissa", Uttarakhand: "Uttaranchal" };
const norm = (n) => NAME_ALIASES[n] || n;

// Source files use ESRI winding; d3-geo needs the opposite orientation
const rewind = (d) => {
  // Normalise both FeatureCollection and GeometryCollection inputs
  const features = d.features
    ? d.features
    : (d.geometries || []).map((g) => ({ type: "Feature", properties: {}, geometry: g }));
  const flip = (coords) => coords.map((ring) => [...ring].reverse());
  features.forEach((f) => {
    if (f.geometry.type === "Polygon") f.geometry.coordinates = flip(f.geometry.coordinates);
    else if (f.geometry.type === "MultiPolygon")
      f.geometry.coordinates = f.geometry.coordinates.map((poly) => flip(poly));
  });
  return { type: "FeatureCollection", features };
};

const IndiaMap = ({ states = [], testId = "india-map" }) => {
  const [statesGeo, setStatesGeo] = useState(null);
  const [outlineGeo, setOutlineGeo] = useState(null);

  useEffect(() => {
    fetch("/assets/india-states.geojson").then((r) => r.json()).then((d) => setStatesGeo(rewind(d))).catch((e) => console.error("India states map failed to load", e));
    fetch("/assets/india-outline.geojson").then((r) => r.json()).then((d) => setOutlineGeo(rewind(d))).catch((e) => console.error("India outline map failed to load", e));
  }, []);

  const { outline, paths, pins } = useMemo(() => {
    if (!statesGeo || !outlineGeo) return { outline: null, paths: [], pins: [] };
    // Fit the projection to the FULL country outline so the J&K crown is never clipped
    const projection = geoMercator().fitExtent([[26, 14], [574, 646]], outlineGeo);
    const pathGen = geoPath(projection);
    const present = new Set(states.map((s) => norm(s.name)));
    return {
      outline: pathGen(outlineGeo.features[0]),
      paths: statesGeo.features.map((f) => ({
        name: f.properties.NAME_1,
        d: pathGen(f),
        active: present.has(f.properties.NAME_1),
      })),
      pins: states
        .map((s) => ({ ...s, pt: projection([s.lng, s.lat]) }))
        .filter((s) => s.pt && s.pt.every(Number.isFinite)),
    };
  }, [statesGeo, outlineGeo, states]);

  return (
    <div className="relative" data-testid={testId}>
      <svg viewBox="0 0 600 660" className="h-auto w-full drop-shadow-[0_30px_60px_rgba(0,0,0,0.35)]" role="img" aria-label="Map of India showing Seed Park presence across 14 states">
        {outline && (
          <path
            d={outline}
            data-testid="map-country-outline"
            fill="rgba(255,255,255,0.05)"
            stroke="rgba(255,255,255,0.45)"
            strokeWidth={1.4}
          />
        )}
        {paths.map((p) => (
          <path
            key={p.name}
            d={p.d}
            data-testid={`map-state-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            fill={p.active ? "rgba(242,112,29,0.4)" : "none"}
            stroke={p.active ? "rgba(245,181,10,0.85)" : "rgba(255,255,255,0.22)"}
            strokeWidth={p.active ? 1.3 : 0.7}
            className="transition-colors duration-300"
          >
            <title>{p.name}</title>
          </path>
        ))}
        {pins.map((s) => (
          <g key={s.name} transform={`translate(${s.pt[0]},${s.pt[1]})`} data-testid={`map-pin-${s.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <circle r={7} fill="#F2701D" opacity={0.4}>
              <animate attributeName="r" values="7;20;7" dur="2.4s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.4;0;0.4" dur="2.4s" repeatCount="indefinite" />
            </circle>
            <circle r={5.5} fill="#F2701D" stroke="#fff" strokeWidth={1.6} />
            <title>{s.name}</title>
          </g>
        ))}
      </svg>
      <div className="mt-4 flex items-center justify-center gap-6 text-[11px] text-white/70" data-testid="map-legend">
        <span className="flex items-center gap-2">
          <span className="inline-block h-3 w-3 rounded-full bg-brand-400" /> Presence state
        </span>
        <span className="flex items-center gap-2">
          <span className="inline-block h-3 w-3 border border-white/30 bg-white/5" /> Other states
        </span>
      </div>
    </div>
  );
};

export default IndiaMap;
